import type { SortSpec } from '@smartsash/types';
import type { TableColumn } from '@smartsash/ui';

/**
 * Client-scope narrowing and ordering (HS-869).
 *
 * `searchScope`/`sortScope: 'client'` keeps the URL as the home of `q` and `sort` — the link is
 * still shareable and survives a reload — while the narrowing happens here, over the page the
 * server already returned. The list endpoints of the admin API accept neither a free-text nor a
 * sort parameter, so the alternative was a control that rewrites the URL and changes nothing.
 *
 * The consequence is deliberate and must be documented per screen: **a client-scope axis sees only
 * the current page**, exactly as the `ui` `Table`'s own uncontrolled behaviour did. Reaching the
 * whole result set needs the API parameters, not more client code.
 *
 * Both functions read text through the column's resolved `value`, so a JSX cell participates as
 * long as its `ColumnDef` carries `meta.value`.
 */
export function filterRowsByQuery<TRow>(
  rows: TRow[],
  columns: TableColumn<TRow>[],
  query: string | undefined,
): TRow[] {
  const trimmed = query?.trim().toLowerCase() ?? '';
  if (trimmed === '') return rows;
  return rows.filter((row) =>
    columns.some((column) => column.value?.(row).toLowerCase().includes(trimmed) ?? false),
  );
}

export function sortRowsByColumn<TRow>(
  rows: TRow[],
  columns: TableColumn<TRow>[],
  sort: SortSpec | undefined,
): TRow[] {
  if (sort == null) return rows;
  const read = columns.find((candidate) => candidate.id === sort.field)?.value;
  if (read == null) return rows;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => read(a).localeCompare(read(b)) * factor);
}
